import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Navbar } from './Navbar';
import { Sidebar, SidebarContent } from './Sidebar';
import { useAuth } from '../context/AuthContext';
import { Toaster } from './ui/sonner';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from './ui/sheet';
import { AlertTriangle, X, Loader2, Menu, Dumbbell } from 'lucide-react';
import { apiPost } from '../api';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';

export const MainLayout = ({ children }) => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <main>{children}</main>
      <Toaster position="top-right" />
    </div>
  );
};

// Impersonation Banner Component
const ImpersonationBanner = () => {
  const [impersonation, setImpersonation] = useState(null);
  const [ending, setEnding] = useState(false);
  const { refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const stored = sessionStorage.getItem('impersonation');
    if (stored) {
      setImpersonation(JSON.parse(stored));
    }
  }, []);

  const handleEndImpersonation = async () => {
    // Iter-46an: ask the backend to swap cookies back to the super-admin's
    // session (using the ss_impersonator cookie it stamped on us). Local
    // setAuth is no longer enough — the ss_access cookie is the source of
    // truth and only the server can rotate it.
    setEnding(true);
    try {
      await apiPost('/api/admin/impersonate/stop');
      sessionStorage.removeItem('impersonation');
      await refreshUser();
      toast.success('Impersonation ended');
      navigate('/admin/studios');
    } catch (e) {
      // If the cookie has already expired / been cleared, fall back to a
      // full logout so the user re-authenticates as themselves.
      toast.error(formatApiError(e, 'Could not restore your session — please log in again'));
      sessionStorage.removeItem('impersonation');
      navigate('/login');
    } finally {
      setEnding(false);
    }
  };

  if (!impersonation?.active) return null;

  return (
    <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm sticky top-0 z-50" data-testid="impersonation-banner">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" />
        <span>
          <strong>Impersonation Mode:</strong> You are viewing as admin of &quot;{impersonation.studioName}&quot; 
          (impersonated by {impersonation.impersonatedBy})
        </span>
      </div>
      <Button 
        size="sm" 
        variant="outline" 
        onClick={handleEndImpersonation}
        disabled={ending}
        className="bg-white/20 hover:bg-white/30 border-white/50 text-white h-7"
        data-testid="end-impersonation-btn"
      >
        {ending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <X className="w-3 h-3 mr-1" />}
        End Session
      </Button>
    </div>
  );
};

export const DashboardLayout = ({ children }) => {
  const { user } = useAuth();
  // Iter-53: track drawer state so mobile nav links can programmatically
  // close the Sheet on click (Radix Sheet doesn't auto-close on internal
  // route changes).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <ImpersonationBanner />
      <div className="flex-1 flex">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile top bar with hamburger — hidden on lg+ where the fixed
              desktop sidebar shows instead. */}
          <header
            className="h-14 border-b bg-background/80 backdrop-blur-md sticky top-0 z-40 px-3 flex items-center gap-3 lg:hidden"
            data-testid="dashboard-mobile-header"
          >
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  aria-label="Open navigation menu"
                  data-testid="mobile-nav-toggle"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-72 p-0 flex flex-col"
                data-testid="mobile-nav-drawer"
              >
                {/* Accessible title + description, visually hidden — Radix requires both. */}
                <SheetTitle className="sr-only">Main navigation</SheetTitle>
                <SheetDescription className="sr-only">
                  Navigate between dashboard sections
                </SheetDescription>
                <SidebarContent onNavigate={() => setMobileNavOpen(false)} />
              </SheetContent>
            </Sheet>

            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center shrink-0">
                <Dumbbell className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-semibold truncate">
                {user?.first_name ? `${user.first_name}'s Dashboard` : 'Dashboard'}
              </span>
            </div>
          </header>
          {/* Iter-53: reduced horizontal padding on mobile (p-4) so page
              content isn't crushed at 375px. Desktop stays p-8 for airy
              layout. `min-w-0` on parent + `overflow-x-hidden` here prevents
              wide children (tables, long headings) from pushing the layout
              sideways. */}
          <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-muted/30 overflow-x-hidden">
            {children}
          </main>
        </div>
      </div>
      <Toaster position="top-right" />
    </div>
  );
};
